import { useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { loadUserSubscribedChannels } from "../../../App/state/actions";
import { trackMediaStart, trackMediaPlay } from "../../../shared/analytics/media";
import constants from "../../../shared/constants";
import PlayerConstants from "../../../shared/constants/player";
import { useReducers } from "../../../shared/hooks/useReducer";

const { REDUCER_TYPE } = constants;
const {
  PLAYER_TYPE: { LIVE },
} = PlayerConstants;

export const useLivePlayerEffects = ({
  isLivePlayer,
  currentChannelId,
  assetId,
  resetRecordingActionValues,
  restartUserInactivityTimer,
  toastNotificationTimer,
  isInitMediaSessionTriggeredTimer,
  loadGuidePrograms,
  getSegmentTimestamp,
  getNextSegmentTimestamp,
  userProfile,
  appProvider,
  subscribedChannels,
  cancelTokenSource,
  isUserProfilesEnabled,
  isPcPinFeatureEnabled,
  isParentalPinEnabled,
  setIsParentalPinEnabled,
  liveProgramDetails,
  programDetailsRef,
  isSwitchChannelRef,
  triggerAnalytics,
  isInitMediaSessionTriggeredRef,
  isInHome,
  getHeartBeatEntitlements,
  setPCBlocked,
  setShowSideNav,
  setShowContentLockedOverlay,
}) => {
  const dispatch = useDispatch();
  const { featureToggles } = useReducers(REDUCER_TYPE.APP);
  const { isDynatraceTrackingEnabled } = featureToggles;
  /**
   * If Parental PIN is enabled, check if the up next program is PC blocked. If it is, show a
   * message and allow the user to choose to enter the parental PIN or change channels.
   */
  const checkProgramPCBlocked = useCallback(() => {
    // If Parental PIN is enabled, check if the up next program is PC blocked
    if (isParentalPinEnabled && currentChannelId && assetId) {
      getHeartBeatEntitlements(currentChannelId, assetId).catch((err) => {
        if (err?.entitlement?.isPCBlocked) {
          // If previous program was not blocked and next program is blocked, or if saved PIN
          // has expired and next program is blocked, pause the player and ask user to input PIN
          setPCBlocked(true);
          setShowSideNav(false);
          setShowContentLockedOverlay(true);
        }
      });
    }
  }, [
    isParentalPinEnabled,
    currentChannelId,
    assetId,
    getHeartBeatEntitlements,
    setPCBlocked,
    setShowSideNav,
    setShowContentLockedOverlay,
  ]);

  useEffect(() => {
    if (isLivePlayer) {
      restartUserInactivityTimer();
      const toastTimer = toastNotificationTimer.current;
      const mediaSessionTimer = isInitMediaSessionTriggeredTimer.current;
      return () => {
        if (toastTimer) {
          clearTimeout(toastTimer);
        }
        if (mediaSessionTimer) {
          clearTimeout(mediaSessionTimer);
        }
        resetRecordingActionValues();
      };
    }
  }, [
    isLivePlayer,
    resetRecordingActionValues,
    restartUserInactivityTimer,
    toastNotificationTimer,
    isInitMediaSessionTriggeredTimer,
  ]);

  useEffect(() => {
    if (isLivePlayer) {
      loadGuidePrograms(getSegmentTimestamp());
      loadGuidePrograms(getNextSegmentTimestamp());
    }
  }, [isLivePlayer, loadGuidePrograms, getSegmentTimestamp, getNextSegmentTimestamp]);

  useEffect(() => {
    if (isLivePlayer && userProfile?.isLoggedIn) {
      // TODO: Add back the recordings data refresh on page load when the backend isn't garbage?
      /* if (isRecordingEnabled && recordingSystemType) {
        // Fetch user's recordings
        getRecordingAction(appProvider, recordingSystemType === RECORDING_PACKAGES.PACKAGE_NAME.LPVRMediaroom_TP,
          true, null, [RECORDING_PARAMS.EVENT], (MR_RECORDING_FILTER_OPTIONS.SCHEDULED_PAGE_FILTERS));
      } */
      if (!subscribedChannels && appProvider) {
        dispatch(
          loadUserSubscribedChannels(appProvider, userProfile, cancelTokenSource.current, isUserProfilesEnabled)
        );
      }
    }
    setIsParentalPinEnabled(
      isPcPinFeatureEnabled && userProfile?.user?.profile?.profileData?.parentalControlPinEnabled === "Y"
    );
  }, [
    isLivePlayer,
    appProvider,
    isPcPinFeatureEnabled,
    userProfile,
    subscribedChannels,
    dispatch,
    isUserProfilesEnabled,
    cancelTokenSource,
    setIsParentalPinEnabled,
  ]);

  useEffect(() => {
    if (isLivePlayer && liveProgramDetails && programDetailsRef.current?.id !== liveProgramDetails?.id) {
      // Check entitlements of subsequent programs on the same channel
      if (programDetailsRef.current?.id && !isSwitchChannelRef.current) {
        checkProgramPCBlocked();
      }

      programDetailsRef.current = liveProgramDetails;

      if (isSwitchChannelRef.current) {
        // triggers a pageLoaded event when switching channel from sidebar
        triggerAnalytics();
      } else if (isInitMediaSessionTriggeredRef.current) {
        trackMediaStart(
          {
            ...programDetailsRef.current.metadata,
            channel: programDetailsRef.current.channel,
          },
          LIVE,
          userProfile,
          appProvider,
          isInHome,
          false,
          isDynatraceTrackingEnabled
        );
        trackMediaPlay({}, isDynatraceTrackingEnabled);
      }
    }
  }, [
    isLivePlayer,
    appProvider,
    checkProgramPCBlocked,
    liveProgramDetails,
    triggerAnalytics,
    userProfile,
    isInHome,
    programDetailsRef,
    isSwitchChannelRef,
    isInitMediaSessionTriggeredRef,
    isDynatraceTrackingEnabled,
  ]);
};
